import React, { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { DescriptionSubtitle } from '../../components/onlineForm/onlineForm';
import Top from '../DevelopersTop/DevelopersTop';
import Keys from '../keys/Keys';
import Webhooks from '../webhooks/Webhooks';
import Members from '../members/Members';
import Settings from '../settingscreen/Settings';
import { MessagePopup } from '../../components/newPopup/messagePopup';
import {
  IMembersData,
  IMember,
  IWebhook,
  MembersPopupType,
  Ikeys,
  IPopupData,
  TestWebhooksPopupType,
  EnvironmentStatus,
  IFieldChangeData,
  Environments,
  DeleteAllDataPopupType,
} from '../../models/developerPortal';
import { AddMemberPopup } from '../../components/newPopup/addMemberPopup';
import { DeleteAllDataPopup } from '../../components/newPopup/deleteAllDataPopup';
import { PopupTheme } from '../../enums/common';
import Title from '../../components/onlineTitle/Title';
import welcome from '../../assets/welcome.svg';

const SContentWrapper = styled.div(
  ({ theme }) => `
  height: 100%;

  @media only screen and ${theme.device.laptop} {
    width: 100%;
  }
`
);

const LeftMenuPageWrapper = styled.div(
  ({ theme }) => `
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;
  @media only screen and ${theme.device.max.laptopS} {
    flex-direction: column;
  }
`
);

const LeftBarMenu = styled.div(
  ({ theme }) => `

  flex-shrink: 0;
  width: 280px;
  padding: 100px 30px;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${theme.colors.disabledBG};
  @media only screen and ${theme.device.max.laptopS} {
    width: 100vw;
    height: 50px;
    flex-direction: row;
    padding: 30px;
    margin-top: 23px;
    border-right: none;
    position: fixed;
  }
`
);

const MenuItem = styled.div(
  ({ theme }) => css`
    padding: 15px 0;
    border: none;
    background: ${theme.colors.white};
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 200px;
    height: 75px;

    > .tab-name {
      font-weight: ${theme.font.weight.medium};
      font-size: 18px;
      color: ${theme.colors.primaryBackground};
      margin-left: 16px;
    }

    &.selected {
      opacity: 1;
      cursor: default;
      color: ${theme.colors.primaryBright};
      background: #f6f9fc;
      font-weight: ${theme.font.weight.bold};
      //border-radius: 30px;
    }

    @media only screen and ${theme.device.max.laptopS} {
      width: 100vw;
      height: 50px;
      flex-direction: row;
    }
  `
);

const PageContent = styled.div(
  ({ theme }) => `
  padding: 100px 60px 50px 60px;
  overflow: auto;
  flex-grow: 1;

  & .hidden {
    display: none;
  }

  @media only screen and ${theme.device.max.laptopS} {
    width: 90vw;
    padding: 140px 45px;
  }
`
);

const SWelcomeContainer = styled.div(
  ({ theme }) => `
  background: ${theme.colors.lightBackground};
  border-radius: ${theme.borderRadiusCommon}px;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 50px;

  ${DescriptionSubtitle} {
    margin: 20px 65px;
  }
  @media only screen and ${theme.device.laptop} {
    width: 100%;
  }
`
);

const SWelcomeImage = styled.img(
  ({ theme }) => `
  width: 154px;
  height: auto;
  margin-bottom: 30px;
`
);

export interface IIntegrationFlowProps {
  logout: () => void;
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  ratesAndTermsUrl: string;
  name: string;
  alliance: string;
  isOwner: boolean;
  userKeys: Array<Ikeys>;
  webhooks: Array<IWebhook>;
  onChange: (fieldChangeData: IFieldChangeData) => void;
  setMemberPopup: (popupStatus: string) => void;
  memberPopupData: IPopupData;
  webhookPopupData: IPopupData;
  setWebhookPopup: (popupStatus: string) => void;
  members: IMembersData[];
  addMembersData: IMember;
  addMembers: () => void;
  prodRequestStatus?: EnvironmentStatus;
  onRequestProdAccess: () => void;
  environmentsWaiting: boolean;
  updateWebhook: (environment: Environments) => Promise<void>;
  testWebhookUrl: (url: string) => Promise<void>;
  setWebhook: (webhook: IWebhook) => void;
  webhooksWaiting: boolean;
  keysWaiting: boolean;
  deleteAllDataPopupData: IPopupData;
  deleteAllData: () => void;
  setDeleteAllDataPopup: (popupStatus: string) => void;
}

interface PopupGenericModel {
  component: React.FC;
  props: object;
  status: string;
}

const IntegrationFlow: React.FC<IIntegrationFlowProps> = ({
  privacyPolicyUrl,
  name,
  isOwner,
  alliance,
  onChange,
  webhooks,
  webhookPopupData,
  setWebhookPopup,
  addMembersData,
  addMembers,
  setMemberPopup,
  memberPopupData,
  members,
  userKeys,
  logout,
  termsOfUseUrl,
  ratesAndTermsUrl,
  prodRequestStatus,
  onRequestProdAccess,
  environmentsWaiting,
  updateWebhook,
  testWebhookUrl,
  setWebhook,
  webhooksWaiting,
  keysWaiting,
  deleteAllDataPopupData,
  deleteAllData,
  setDeleteAllDataPopup,
}) => {
  const [selectedTab, setSelectedTab] = useState('Welcome');

  const welcomeContent = useMemo(() => {
    return (
      <SWelcomeContainer>
        <SWelcomeImage src={welcome} />

        <Title>Hi {name.split(' ')[0]},</Title>

        <DescriptionSubtitle>
          Welcome to {alliance} team on Sunbit’s developer portal! <br />
          <br />
          Here you have everything you need to build, test and manage your integration with Sunbit’s BNPL products.
        </DescriptionSubtitle>
      </SWelcomeContainer>
    );
  }, [alliance, name, welcome]);

  const keysContent = useMemo(() => {
    return (
      <Keys
        {...{
          userKeys,
          prodRequestStatus,
          onRequestProdAccess,
          environmentsWaiting,
          waiting: keysWaiting,
        }}
      />
    );
  }, [userKeys, prodRequestStatus, onRequestProdAccess, environmentsWaiting, keysWaiting]);

  const webhooksContent = useMemo(() => {
    return (
      <Webhooks
        {...{
          webhooks,
          testWebhookUrl,
          updateWebhook,
          prodRequestStatus,
          setWebhook,
          waiting: webhooksWaiting,
        }}
      />
    );
  }, [webhooks, testWebhookUrl, updateWebhook, prodRequestStatus, setWebhook, webhooksWaiting]);

  const tabs = useMemo(() => {
    const handlePopupState = () => {
      memberPopupData.status === '' ? setMemberPopup('addMember') : setMemberPopup('');
    };

    const handleDeleteAllDataPopupState = () => {
      memberPopupData.status === '' ? setDeleteAllDataPopup('deleteAllData') : setDeleteAllDataPopup('');
    };

    const settingsContent = (
      <Settings
        {...{
          handlePopupState: handleDeleteAllDataPopupState,
        }}
      />
    );

    const membersContent = (
      <Members
        {...{
          isOwner,
          handlePopupState,
          members,
        }}
      />
    );

    return [
      { name: 'Welcome', content: welcomeContent },
      { name: 'Members', content: membersContent },
      { name: 'Keys', content: keysContent },
      { name: 'Webhooks', content: webhooksContent },
      { name: 'Settings', content: settingsContent },
    ].map((t) => ({ ...t, onClick: () => setSelectedTab(t.name) }));
  }, [
    welcomeContent,
    environmentsWaiting,
    memberPopupData,
    setMemberPopup,
    isOwner,
    members,
    prodRequestStatus,
    onRequestProdAccess,
    keysContent,
    webhooksContent,
    deleteAllDataPopupData,
    setDeleteAllDataPopup,
  ]);

  const popup = useMemo<PopupGenericModel>(() => {
    const handleMemberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ value: e.target.value, fieldName: e.target.name });
    };

    const handleMemberPopupState = () => {
      memberPopupData.status === '' ? setMemberPopup('addMember') : setMemberPopup('');
    };

    const handleWebhooksPopupState = () => {
      memberPopupData.status === '' ? setWebhookPopup('addMember') : setWebhookPopup('');
    };

    const handleDeleteAllDataPopupState = () => {
      deleteAllDataPopupData.status === '' ? setDeleteAllDataPopup('deleteAllData') : setDeleteAllDataPopup('');
    };

    const popups = [
      {
        status: MembersPopupType.addMember,
        component: AddMemberPopup,
        props: {
          dataAt: 'addMemberPopup',
          boldText: 'Add member',
          grayButtonText: 'Exit',
          blueButtonText: 'Invite',
          blueButtonAction: addMembers,
          grayButtonAction: handleMemberPopupState,
          fieldList: [
            {
              fieldLabel: 'First Name',
              fieldName: 'memberFirstName',
              inputData: { value: addMembersData.memberFirstName.value, error: addMembersData.memberFirstName.error },
            },
            {
              fieldLabel: 'Last Name',
              fieldName: 'memberLastName',
              inputData: { value: addMembersData.memberLastName.value, error: addMembersData.memberLastName.error },
            },
            {
              fieldLabel: 'email',
              fieldName: 'email',
              inputData: { value: addMembersData.email.value, error: addMembersData.email.error },
            },
          ],
          handleMemberChange: handleMemberChange,
        },
      },
      {
        status: DeleteAllDataPopupType.deleteAllData,
        component: DeleteAllDataPopup,
        props: {
          dataAt: 'deletAllDataPopup',
          boldText: 'Are you sure you want to delete all of your data stored in your demo environment?',
          grayButtonText: 'Cancel',
          blueButtonText: 'Confirm',
          blueButtonAction: deleteAllData,
          grayButtonAction: handleDeleteAllDataPopupState,
          italic:
            'This will delete all data including retailers, applications, and accounts. This process cannot be undone.',
        },
      },
      {
        status: MembersPopupType.addMemberSuccess,
        component: MessagePopup,
        props: {
          dataAt: 'addMemberSuccessPopup',
          boldText: '',
          blueButtonText: 'Close',
          blueButtonAction: handleMemberPopupState,
          messagePopupData: {
            title: memberPopupData.title,
            description: memberPopupData.description,
            status: PopupTheme.success,
          },
        },
      },
      {
        status: MembersPopupType.addMemberFail,
        component: MessagePopup,
        props: {
          dataAt: 'addMemberFailPopup',
          boldText: '',
          blueButtonText: 'Close',
          blueButtonAction: handleMemberPopupState,
          messagePopupData: {
            title: memberPopupData.title,
            description: memberPopupData.description,
            status: PopupTheme.danger,
          },
        },
      },
      {
        status: TestWebhooksPopupType.testWebhookSuccess,
        component: MessagePopup,
        props: {
          dataAt: 'testWebhookSuccessPopup',
          boldText: '',
          blueButtonText: 'Close',
          blueButtonAction: handleWebhooksPopupState,
          messagePopupData: {
            title: '',
            description: 'Test completed successfully',
            status: PopupTheme.success,
          },
        },
      },
      {
        status: TestWebhooksPopupType.testWebhookFail,
        component: MessagePopup,
        props: {
          dataAt: 'testWebhookFailPopup',
          boldText: '',
          blueButtonText: 'Close',
          blueButtonAction: handleWebhooksPopupState,
          messagePopupData: {
            title: '',
            description: 'Test failed',
            status: PopupTheme.danger,
          },
        },
      },
    ];

    return (
      popups.find((p) =>
        [memberPopupData.status, webhookPopupData.status, deleteAllDataPopupData.status].includes(p.status)
      ) || ({} as PopupGenericModel)
    );
  }, [memberPopupData, onChange, addMembers, addMembersData, webhookPopupData, deleteAllDataPopupData, deleteAllData]);

  const { component: PopupComponent, props: popupProps } = popup;

  return (
    <SContentWrapper data-at="contentWrapper">
      <Top
        privacyPolicyUrl={privacyPolicyUrl}
        termsOfUseUrl={termsOfUseUrl}
        ratesAndTermsUrl={ratesAndTermsUrl}
        showProfileThumb={true}
        showDeveloperDocuments={true}
        headerTitleText=""
        username={name}
        logout={logout}
      />
      <LeftMenuPageWrapper className="wrapper">
        <LeftBarMenu>
          {tabs.map(({ name, onClick }) => (
            <MenuItem
              {...{
                key: `menu-${name}`,
                className: selectedTab === name ? 'selected' : '',
                onClick: onClick,
              }}
            >
              <p className="tab-name">{name}</p>
            </MenuItem>
          ))}
        </LeftBarMenu>

        <PageContent>{tabs.find((t) => t.name === selectedTab).content}</PageContent>
        {!!PopupComponent && <PopupComponent {...popupProps} />}
      </LeftMenuPageWrapper>
    </SContentWrapper>
  );
};

export default IntegrationFlow;
